// Builds the onboarding journey an agent sees on a prospect: every step of the
// pipeline, split into what is done, what is happening now, and what is still
// outstanding. Pure — no API, no React — so the branching below can be tested
// exhaustively without rendering anything.

import {
  JourneyStep,
  JourneyStepId,
  JourneyStepStatus,
  OnboardingState,
  SetupFee,
  StateTransition,
} from "../types/onboarding";
import { PIPELINE_STATES, pipelineIndex, stateLabel } from "./onboardingStates";
import { formatSetupFeeAmount } from "./setupFee";

interface BuildJourneyParams {
  currentState: OnboardingState;
  transitions: StateTransition[];
  moreInfoNeeded?: boolean;
  // Null or absent until the prospect is promoted, and while it loads.
  setupFee?: SetupFee | null;
}

// ESIGN_AWAITED is a branch of the digital signing track and deliberately not in
// PIPELINE_STATES. It sits at the same point of the journey as CONTRACT_SENT, so
// that is the step shown as current, with a note explaining the wait.
const BRANCH_STATES: Partial<
  Record<OnboardingState, { step: OnboardingState; note: string }>
> = {
  ESIGN_AWAITED: {
    step: "CONTRACT_SENT",
    note: "Awaiting the customer's signature.",
  },
};

const MORE_INFO_NOTE = "More information is needed from the customer.";

const SKIPPED_NOTE = "No record of this step — the prospect moved past it.";

// Billing is added when the prospect is promoted, so no prospect ever sits on
// this step: it printed "no record of this step" on every single journey, which
// teaches an agent to read past a row rather than to read it. It stays in the
// pipeline — the state exists, the backend can set it, and the station panel
// gates on it — and it is shown whenever a prospect is actually on it or
// something recorded it. It is dropped only when it has nothing to say.
const NEVER_ENTERED_STATES: JourneyStepId[] = ["STATION_ADDED_TO_EROUND"];

const carriesNothing = (step: JourneyStep): boolean =>
  NEVER_ENTERED_STATES.includes(step.state) &&
  step.status !== "current" &&
  step.status !== "done";

// The step a prospect counts as being on, for a state that is not itself part of
// the ordered pipeline. Returns null when the journey has no current step at all
// (a resigned prospect is not paused on a step — it stopped).
const resolveCurrentStep = (state: OnboardingState): OnboardingState | null => {
  if (BRANCH_STATES[state]) return BRANCH_STATES[state]!.step;
  if (pipelineIndex(state) !== -1) return state;
  return null;
};

// The step a recorded transition counts for. The digital track never records
// CONTRACT_SENT — marking the e-sign request as sent records ESIGN_AWAITED — so
// that transition is the record of the contract going out.
const recordedStep = (state: OnboardingState): OnboardingState =>
  BRANCH_STATES[state]?.step ?? state;

const SETUP_FEE_ACTION = "(More actions → Setup fee…)";

// The setup fee (CWA-196) is decided beside the pipeline, not as a state of it:
// it never blocks the next step, and customers promoted long ago still owe it.
// So it is never "current" — the prospect is on its pipeline step — and an
// undecided fee says what is owed in its note instead.
const setupFeeStep = (
  fee: SetupFee | null | undefined,
  promoted: JourneyStepStatus
): JourneyStep => {
  const decided = fee?.status === "INVOICED" || fee?.status === "WAIVED";
  let status: JourneyStepStatus;
  if (decided) status = "done";
  else if (promoted === "not-reached") status = "not-reached";
  else status = "pending";

  let note: string | null = null;
  if (fee?.status === "OPEN") {
    note = `Open: create the invoice or waive the fee ${SETUP_FEE_ACTION}.`;
  } else if (fee?.status === "INVOICING") {
    note = `Invoice requested but not confirmed by billing: retry it ${SETUP_FEE_ACTION}.`;
  } else if (fee?.status === "INVOICED") {
    const title = fee.invoice?.title ? ` ${fee.invoice.title}` : "";
    note = `Draft invoice${title} (${formatSetupFeeAmount(fee.gross_amount_cents)}) created; finance finalises it in Easybill.`;
  } else if (fee?.status === "WAIVED") {
    note = `Waived: ${fee.waiver_reason ?? ""}`.trim();
  }

  return {
    state: "SETUP_FEE",
    label: "Setup fee",
    status,
    completed_at: decided ? fee?.decided_at ?? null : null,
    note,
    actor: decided ? fee?.decided_by ?? null : null,
    actor_role: decided && fee?.decided_by ? "OPS" : null,
    resign_reason: null,
    resign_note: null,
  };
};

export const buildJourney = ({
  currentState,
  transitions,
  moreInfoNeeded = false,
  setupFee = null,
}: BuildJourneyParams): JourneyStep[] => {
  const ended = currentState === "RESIGNED";
  const currentStep = resolveCurrentStep(currentState);
  const currentIndex = currentStep ? pipelineIndex(currentStep) : -1;

  // For an ended journey, the last step actually reached is the furthest one
  // with a recorded transition — the prospect stopped somewhere, and everything
  // after that was never reached rather than merely pending.
  const lastReachedIndex = ended
    ? transitions.reduce((furthest, t) => {
        const idx = pipelineIndex(recordedStep(t.state));
        return idx > furthest ? idx : furthest;
      }, -1)
    : currentIndex;

  // First transition recorded per state. Whether a state has one at all is what
  // separates a step that happened from one the prospect merely passed. The
  // whole transition is kept, not just its timestamp: the actor and the
  // resignation details used to live in a second timeline that duplicated this.
  const recorded = new Map<OnboardingState, StateTransition>();
  transitions.forEach((t) => {
    const step = recordedStep(t.state);
    const existing = recorded.get(step);
    if (
      !existing ||
      new Date(t.transitioned_at).getTime() <
        new Date(existing.transitioned_at).getTime()
    ) {
      recorded.set(step, t);
    }
  });

  // A resigned prospect's reason and note hang off the RESIGNED transition,
  // which is not a pipeline step — they belong on the last step it reached.
  const resignation = transitions.find((t) => t.state === "RESIGNED");

  const steps: JourneyStep[] = PIPELINE_STATES.map((state) => {
    const index = pipelineIndex(state);

    const hasRecord = recorded.has(state);

    let status: JourneyStepStatus;
    if (ended) {
      // No "current" step on an ended journey.
      if (index > lastReachedIndex) status = "not-reached";
      else status = hasRecord ? "done" : "skipped";
    } else if (index < currentIndex) {
      // Being past a step is not the same as having done it. Several pipeline
      // states are optional — promotion is allowed straight from
      // CONTRACT_SIGNED, so STATION_ADDED_TO_EROUND and
      // OCPP_CREDENTIALS_GENERATED are routinely never entered and carry no
      // transition. Ticking those green would claim something that never
      // happened, so a missing record reads as skipped instead.
      status = hasRecord ? "done" : "skipped";
    } else if (index === currentIndex) {
      status = "current";
    } else {
      status = "pending";
    }

    let note: string | null = null;
    if (status === "current") {
      const branch = BRANCH_STATES[currentState];
      if (branch && branch.step === state) note = branch.note;
      if (moreInfoNeeded) note = note ? `${note} ${MORE_INFO_NOTE}` : MORE_INFO_NOTE;
    } else if (status === "skipped") {
      // Deliberately not "did not happen" — the step may well have been done
      // outside the system (a station added to billing by hand, say). All we
      // can honestly say is that nothing recorded it.
      note = SKIPPED_NOTE;
    }

    const transition = recorded.get(state);
    const reached = status === "done" || status === "current";
    const isLastReached = ended && index === lastReachedIndex;

    return {
      state,
      label: stateLabel(state),
      status,
      completed_at: reached ? transition?.transitioned_at ?? null : null,
      note,
      actor: reached ? transition?.triggered_by ?? null : null,
      actor_role: reached ? transition?.triggered_by_role ?? null : null,
      resign_reason: isLastReached ? resignation?.resign_reason ?? null : null,
      resign_note: isLastReached ? resignation?.resign_note ?? null : null,
    };
  });

  const promotedAt = steps.findIndex((step) => step.state === "CUSTOMER_PROMOTED");
  steps.splice(promotedAt + 1, 0, setupFeeStep(setupFee, steps[promotedAt].status));

  return steps.filter((step) => !carriesNothing(step));
};

// True when the journey stopped rather than paused — the caller uses this to
// avoid claiming a next action.
export const isJourneyEnded = (currentState: OnboardingState): boolean =>
  currentState === "RESIGNED";
